import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import CelebrationBanner from "../components/CelebrationBanner";
import CelebrationsWidget from "../components/CelebrationsWidget";
import {
  LayoutDashboard,
  TrendingUp,
  Calculator,
  Landmark,
  GraduationCap,
  KeyRound,
  Contact,
  FileSignature,
  FileText,
  MessageSquare,
  Users,
  Banknote,
  BookOpen,
  LayoutList,
  ClipboardList,
  FileSignature as FileSignatureIcon2,
} from "lucide-react";

const ModuleCard = ({ to, icon: Icon, title, description, accent, testId, external, crossDomain }) => {
  const className =
    "group relative flex flex-col gap-3 p-5 bg-card border border-border rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:border-foreground/20 transition-all duration-200 overflow-hidden";

  const content = (
    <>
      <div
        className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{
          background: `radial-gradient(circle at top left, ${accent}14, transparent 60%)`,
        }}
      />

      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center relative"
        style={{ backgroundColor: `${accent}1A`, color: accent }}
      >
        <Icon size={20} strokeWidth={2} />
      </div>

      <div className="space-y-1 relative">
        <h3 className="text-base font-semibold text-foreground tracking-tight leading-tight">
          {title}
        </h3>
        <p className="text-xs text-muted-foreground leading-snug">
          {description}
        </p>
      </div>
    </>
  );

  // Cross-domain link: inject the user's current JWT for one-shot SSO handoff
  if (crossDomain) {
    const handleClick = (e) => {
      e.preventDefault();
      const token = localStorage.getItem("token") || "";
      const finalUrl = to.replace("__TOKEN__", encodeURIComponent(token));
      window.open(finalUrl, "_blank", "noopener,noreferrer");
    };
    return (
      <a href="#" onClick={handleClick} className={className} data-testid={testId}>
        {content}
      </a>
    );
  }

  // External (same-domain subpath) link: anchor tag so React Router doesn't intercept it
  if (external) {
    return (
      <a href={to} className={className} data-testid={testId}>
        {content}
      </a>
    );
  }

  return (
    <Link to={to} className={className} data-testid={testId}>
      {content}
    </Link>
  );
};

const Home = () => {
  const { user, isAdmin, isSupervisor, tracksStats, tracksBillings } = useAuth();

  const showPersonalStats = tracksStats || tracksBillings;

  // ---------- Module groups (mirrors the sidebar grouping) ----------
  // Each section is built only with tiles relevant to this user's role. Empty
  // groups auto-collapse out so a non-admin doesn't see an "Overview" header
  // with nothing under it.
  const overview = [];
  if (isAdmin) {
    overview.push({
      to: "/admin",
      icon: LayoutDashboard,
      title: "Admin",
      description: "Firm overview, employees, stats entry, billings & benchmarks",
      accent: "#2563EB",
      testId: "home-module-admin",
    });
  }
  if (isSupervisor && !isAdmin) {
    overview.push({
      to: "/supervisor",
      icon: Users,
      title: "Employee Stats",
      description: "Monitor employee performance metrics",
      accent: "#8B5CF6",
      testId: "home-module-supervisor",
    });
  }
  if (showPersonalStats) {
    overview.push({
      to: "/dashboard",
      icon: TrendingUp,
      title: "My Stats",
      description: "Your personal performance vs firm benchmarks",
      accent: "#10B981",
      testId: "home-module-my-stats",
    });
  }

  const onShelf = user?.shelf_participant !== false;

  const clientWork = [
    ...(onShelf ? [{
      to: "/shelf",
      icon: LayoutList,
      title: "Digital Shelf",
      description: "Claim open work off the shared shelf",
      accent: "#F97316",
      testId: "home-module-digital-shelf",
    }] : []),
    ...(isAdmin && onShelf
      ? [
          {
            to: "/shelf/board",
            icon: ClipboardList,
            title: "Shelf Board",
            description: "All work, load, leaderboard & queue metrics",
            accent: "#22D3EE",
            testId: "home-module-shelf-board",
          },
        ]
      : []),
    {
      to: "/tax-calculator",
      icon: Calculator,
      title: "Tax Calculator",
      description: "2026 federal & state tax projections",
      accent: "#0EA5E9",
      testId: "home-module-tax-calculator",
    },
    {
      to: "/amortization",
      icon: Landmark,
      title: "Amortization",
      description: "Loan amortization schedules & calculations",
      accent: "#F59E0B",
      testId: "home-module-amortization",
    },
    {
      to: "/templates",
      icon: FileSignature,
      title: "Firm Templates",
      description: "Client instruction letters & AI-powered drafts",
      accent: "#14B8A6",
      testId: "home-module-templates",
    },
    {
      to: "/w9",
      icon: FileSignatureIcon2,
      title: "Request W-9",
      description: "Send & track W-9 requests to vendors",
      accent: "#6366F1",
      testId: "home-module-w9",
    },
    {
      to: "https://clientfinancials.steelemartin.com/sso?token=__TOKEN__",
      icon: Banknote,
      title: "Client Financial Platform",
      description: "Client books, financials & reports",
      accent: "#059669",
      testId: "home-module-client-financials",
      external: true,
      crossDomain: true,
    },
  ];

  const resources = [
    {
      to: "/contacts",
      icon: Contact,
      title: "Contact Directory",
      description: "Firm-wide contact list & lookup",
      accent: "#06B6D4",
      testId: "home-module-contacts",
    },
    {
      to: "/handbook",
      icon: BookOpen,
      title: "Employee Handbook",
      description: "Firm policies, benefits & guidelines",
      accent: "#3B82F6",
      testId: "home-module-handbook",
    },
    {
      to: "/training",
      icon: GraduationCap,
      title: "Training",
      description: "Firm training materials, videos & resources",
      accent: "#EC4899",
      testId: "home-module-training",
    },
    {
      to: "/documents",
      icon: FileText,
      title: "Documents",
      description: "Firm documents & your personal files",
      accent: "#6366F1",
      testId: "home-module-documents",
    },
  ];

  const tools = [
    {
      to: "/passwords",
      icon: KeyRound,
      title: "Password Manager",
      description: "Generate secure, readable passwords",
      accent: "#A855F7",
      testId: "home-module-passwords",
    },
    {
      to: "/feedback",
      icon: MessageSquare,
      title: "Feedback",
      description: "Give and receive feedback across the team",
      accent: "#F43F5E",
      testId: "home-module-feedback",
    },
  ];

  const sections = [
    { label: "Overview", modules: overview },
    { label: "Client Work", modules: clientWork },
    { label: "Resources", modules: resources },
    { label: "Tools", modules: tools },
  ].filter((s) => s.modules.length > 0);

  const firstName = user?.name?.split(" ")[0] || "there";
  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="space-y-8 animate-fade-in" data-testid="home-page">
      <CelebrationBanner />

      {/* Welcome header */}
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-foreground tracking-tight">
          {greeting}, {firstName}.
        </h1>
        <p className="text-muted-foreground mt-1">
          {user?.position
            ? `${user.position} · Choose a module to get started`
            : "Choose a module to get started"}
        </p>
      </div>

      <CelebrationsWidget />

      {/* Grouped module sections */}
      <div className="space-y-8" data-testid="home-module-sections">
        {sections.map((section) => (
          <section
            key={section.label}
            data-testid={`home-section-${section.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/80 mb-3">
              {section.label}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {section.modules.map((m) => (
                <ModuleCard key={m.to} {...m} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default Home;
